import { 
  Monitor, 
  Smartphone, 
  Server, 
  Printer,
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Asset } from './AssetList';

const typeIcons = {
  desktop: Monitor,
  laptop: Monitor,
  server: Server,
  printer: Printer,
  mobile: Smartphone,
};

const statusConfig = {
  active: { label: 'Active', icon: CheckCircle, className: 'bg-success/20 text-success border-success/30' },
  inactive: { label: 'Inactive', icon: XCircle, className: 'bg-muted text-muted-foreground border-border' },
  maintenance: { label: 'Maintenance', icon: AlertCircle, className: 'bg-warning/20 text-warning border-warning/30' },
};

interface AssetDetailProps {
  asset: Asset;
  onEdit?: () => void;
  onRemoteConnect?: () => void;
}

export function AssetDetail({ asset, onEdit, onRemoteConnect }: AssetDetailProps) {
  const Icon = typeIcons[asset.type];

  return (
    <div className="flex-1 flex flex-col bg-card">
      <div className="h-12 border-b border-border flex items-center justify-between px-4">
        <span className="text-sm font-mono text-muted-foreground">{asset.id}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover border-border z-50">
            <DropdownMenuItem onClick={onEdit}>Edit Asset</DropdownMenuItem>
            <DropdownMenuItem>View History</DropdownMenuItem>
            <DropdownMenuItem onClick={onRemoteConnect}>Remote Connect</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive">Retire Asset</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ScrollArea className="flex-1 p-6">
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-xl bg-secondary flex items-center justify-center">
              <Icon className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">{asset.name}</h1>
              <Badge className={statusConfig[asset.status].className}>
                {statusConfig[asset.status].label}
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Type</span>
              <p className="text-sm font-medium text-foreground mt-1 capitalize">{asset.type}</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Department</span>
              <p className="text-sm font-medium text-foreground mt-1">{asset.department}</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Operating System</span>
              <p className="text-sm font-medium text-foreground mt-1">{asset.os}</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">IP Address</span>
              <p className="text-sm font-medium text-foreground mt-1 font-mono">{asset.ip}</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Assigned To</span>
              <p className="text-sm font-medium text-foreground mt-1">{asset.assignedTo || 'Unassigned'}</p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Recent Activity</h3>
            <div className="space-y-2">
              {[
                { action: 'System update installed', time: '2 hours ago' },
                { action: 'User logged in', time: '3 hours ago' },
                { action: 'Security scan completed', time: '1 day ago' },
                { action: 'Software installed: Visual Studio Code', time: '2 days ago' },
              ].map((activity, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-sm text-foreground">{activity.action}</span>
                  <span className="text-xs text-muted-foreground">{activity.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
